/**
 * transcribeEngine — browser-side audio/video → text orchestration.
 *
 * The whole pipeline runs on the user's device: we decode the media to a 16 kHz
 * mono Float32Array with the Web Audio API, then hand that to a Web Worker running
 * Whisper (whisperWorker.js). The original video/audio bytes NEVER leave the
 * browser and are never persisted — matching the product rule "we do not save any
 * videos". Only the resulting text (and, if the user chooses, the source link) is
 * kept, and only in the Knowledge Vault.
 *
 * Mirrors the existing free-browser engine pattern (Tesseract/pdfjs) used by the
 * bank-statement extractor: lazy heavy work + a label/percent progress callback.
 */

export type ModelSize = 'tiny' | 'base' | 'small' | 'medium' | 'large-v3';
export type TranscribeTask = 'transcribe' | 'translate';

export interface ModelOption {
  size: ModelSize;
  modelId: string;
  label: string;
  note: string;
  approxMB: number;
}

// Whisper sizes, smallest→largest. Bigger = more accurate (esp. Hindi + accented
// English) but a larger one-time download + slower on CPU. Values are the quantized
// download footprints transformers.js actually fetches.
export const MODELS: ModelOption[] = [
  { size: 'tiny',     modelId: 'Xenova/whisper-tiny',     label: 'Tiny — fastest',        note: 'Quick drafts; weakest on Hindi.',                approxMB: 42 },
  { size: 'base',     modelId: 'Xenova/whisper-base',     label: 'Base — fast',           note: 'Good English; passable Hindi.',                  approxMB: 78 },
  { size: 'small',    modelId: 'Xenova/whisper-small',    label: 'Small — balanced ★',    note: 'Recommended default. Solid Hindi + English.',    approxMB: 256 },
  { size: 'medium',   modelId: 'Xenova/whisper-medium',   label: 'Medium — accurate',     note: 'Noticeably better Hindi/Indian-English. Slower.', approxMB: 800 },
  { size: 'large-v3', modelId: 'Xenova/whisper-large-v3', label: 'Large-v3 — best',       note: 'Near-perfect, but heavy — best with a strong PC.', approxMB: 1500 },
];

export function modelForSize(size: ModelSize): ModelOption {
  return MODELS.find((m) => m.size === size) || MODELS[2];
}

export interface LanguageOption { code: string; label: string; whisper?: string; }
// 'auto' lets Whisper detect. The rest cover the user's stated content.
export const LANGUAGES: LanguageOption[] = [
  { code: 'auto', label: 'Auto-detect' },
  { code: 'hi',   label: 'Hindi',   whisper: 'hindi' },
  { code: 'en',   label: 'English (US / India)', whisper: 'english' },
];

export function whisperLangName(code: string): string | undefined {
  const l = LANGUAGES.find((x) => x.code === code);
  return l?.whisper; // undefined for 'auto'
}

export interface TranscriptChunk { timestamp: [number, number | null]; text: string; }
export interface TranscriptResult {
  text: string;
  chunks: TranscriptChunk[];
  durationSec: number;
  detectedLanguageHint?: string;
}

export type ProgressStage = 'decode' | 'download' | 'transcribe' | 'done';
export interface ProgressInfo { stage: ProgressStage; label: string; pct: number | null; }
export type ProgressCb = (p: ProgressInfo) => void;

export interface TranscribeOptions {
  size: ModelSize;
  language: string;      // 'auto' | 'hi' | 'en'
  task: TranscribeTask;
  timestamps: boolean;   // true → segment timestamps (for SRT/VTT)
  signal?: AbortSignal;
}

const TARGET_SR = 16000;

/** Decode any browser-playable audio/video file into a 16 kHz mono Float32Array. */
export async function decodeToMono16k(
  file: File | Blob,
  onProgress?: ProgressCb,
): Promise<{ audio: Float32Array; durationSec: number }> {
  onProgress?.({ stage: 'decode', label: 'Reading & decoding audio…', pct: null });
  const arrayBuf = await file.arrayBuffer();

  const AC: typeof AudioContext =
    (window as any).AudioContext || (window as any).webkitAudioContext;
  if (!AC) throw new Error('Web Audio API is unavailable in this browser.');

  const ctx = new AC();
  let decoded: AudioBuffer;
  try {
    // decodeAudioData handles wav/mp3/m4a/aac/ogg/flac and the audio track of most
    // mp4/mov/webm videos in Chromium/Edge.
    decoded = await ctx.decodeAudioData(arrayBuf.slice(0));
  } catch (err) {
    await ctx.close().catch(() => {});
    throw new Error(
      'Could not decode this file’s audio in the browser. Try an audio export ' +
      '(MP3, WAV, M4A) or an MP4 with a standard AAC track.',
    );
  }
  const durationSec = decoded.duration;

  // Downmix to mono + resample to 16 kHz via an OfflineAudioContext render.
  const frames = Math.max(1, Math.ceil(durationSec * TARGET_SR));
  const OAC: typeof OfflineAudioContext =
    (window as any).OfflineAudioContext || (window as any).webkitOfflineAudioContext;
  const offline = new OAC(1, frames, TARGET_SR);
  const src = offline.createBufferSource();
  src.buffer = decoded;
  src.connect(offline.destination);
  src.start(0);
  const rendered = await offline.startRendering();
  const audio = rendered.getChannelData(0).slice(); // copy out before ctx GC
  await ctx.close().catch(() => {});
  return { audio, durationSec };
}

let _worker: Worker | null = null;
function getWorker(): Worker {
  if (!_worker) {
    _worker = new Worker(new URL('./whisperWorker.js', import.meta.url));
  }
  return _worker;
}

let _jobSeq = 0;

/** Full pipeline: decode → (download model) → transcribe. Resolves with the text. */
export async function transcribeFile(
  file: File | Blob,
  opts: TranscribeOptions,
  onProgress?: ProgressCb,
): Promise<TranscriptResult> {
  const { audio, durationSec } = await decodeToMono16k(file, onProgress);
  if (opts.signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  const model = modelForSize(opts.size);
  const worker = getWorker();
  const jobId = ++_jobSeq;

  // Aggregate multi-file model-download progress into a single percent.
  const dl: Record<string, { loaded: number; total: number }> = {};

  return await new Promise<TranscriptResult>((resolve, reject) => {
    const cleanup = () => {
      worker.removeEventListener('message', onMsg);
      if (opts.signal) opts.signal.removeEventListener('abort', onAbort);
    };
    const onAbort = () => {
      cleanup();
      // A worker mid-inference can't be interrupted cleanly; drop it so the next
      // run starts fresh rather than delivering a stale result.
      try { worker.terminate(); } catch { /* noop */ }
      _worker = null;
      reject(new DOMException('Aborted', 'AbortError'));
    };
    const onMsg = (e: MessageEvent) => {
      const m = e.data || {};
      if (m.jobId !== jobId) return;
      if (m.type === 'download') {
        if (m.total) dl[m.file] = { loaded: m.loaded || 0, total: m.total };
        const tot = Object.values(dl).reduce((a, b) => a + b.total, 0);
        const got = Object.values(dl).reduce((a, b) => a + b.loaded, 0);
        const pct = tot ? got / tot : (typeof m.progress === 'number' ? m.progress / 100 : null);
        onProgress?.({ stage: 'download', label: `Downloading ${model.label.split(' —')[0]} model…`, pct });
      } else if (m.type === 'status') {
        if (m.status === 'transcribing') {
          onProgress?.({ stage: 'transcribe', label: 'Transcribing…', pct: null });
        } else if (m.status === 'loading-model') {
          onProgress?.({ stage: 'download', label: 'Preparing model…', pct: null });
        }
      } else if (m.type === 'result') {
        cleanup();
        onProgress?.({ stage: 'done', label: 'Done', pct: 1 });
        resolve({
          text: m.text || '',
          chunks: (m.chunks || []) as TranscriptChunk[],
          durationSec,
        });
      } else if (m.type === 'error') {
        cleanup();
        reject(new Error(m.message || 'Transcription failed'));
      }
    };
    worker.addEventListener('message', onMsg);
    if (opts.signal) opts.signal.addEventListener('abort', onAbort);

    worker.postMessage(
      {
        type: 'run',
        jobId,
        audio,
        modelId: model.modelId,
        language: whisperLangName(opts.language),
        task: opts.task,
        returnTimestamps: opts.timestamps,
      },
      [audio.buffer], // transfer, not copy
    );
  });
}

/** Free the worker (and its loaded model) — e.g. when leaving the page. */
export function disposeTranscriber() {
  if (_worker) { try { _worker.terminate(); } catch { /* noop */ } _worker = null; }
}
